import { Button, Layout, LayoutWidth, Loader, Skeleton, TabPane, Tabs, icons } from '@lifebit-ai/lds'
import React from 'react'

import ApprovalModal from 'components/ApprovalModal/ApprovalModal'
import RejectionModal from 'components/RejectionModal/RejectionModal'
import { CommonContainer, LoadingWrapper } from 'core/Common.styles'
import { ResultMetaV1 } from 'modules/requests/ResultMetaV1'
import { RequestActivityCard } from 'modules/requests/components/RequestActivity/RequestActivityCard'
import { RequestDetailLayout } from 'modules/requests/components/RequestDetailLayout/RequestDetailLayout'
import { RequestLabelsCard } from 'modules/requests/components/RequestLabelsCard/RequestLabelsCard'
import { ApprovalModalDescription } from 'modules/resultsV2/ApprovalModalDescription'
import Reports from 'modules/resultsV2/Reports'
import ResultAlerts from 'modules/resultsV2/ResultAlerts'
import ResultMeta from 'modules/resultsV2/ResultMeta'
import {
  ButtonsWrapper,
  ContentsTitle,
  GoUpContainer,
  GoUpIconContainer,
  HeaderSubtitle,
  ReportMessage,
  ResultContainer,
  StyledContent,
  StyledReportMessage,
  SubtitleSkeletonWrapper,
  TitleSkeletonWrapper
} from 'modules/resultsV2/Results.styles'
import ResultsList from 'modules/resultsV2/ResultsList'
import Tags from 'modules/resultsV2/Tags'
import DownloadExpirySelector from 'modules/resultsV2/components/DownloadExpirySelector'
import RequestAttestationModal from 'modules/resultsV2/components/RequestAttestationModal'
import useResults from 'modules/resultsV2/hooks/useResults'
import {
  APPROVE_BUTTON_TEST_ID,
  REJECT_BUTTON_TEST_ID,
  REQUEST_ATTESTATION_BUTTON_TEST_ID,
  downloadExpiryOptions
} from 'modules/resultsV2/utils'
import { toExportRequestActivitySource } from 'services/request-activity/mappers'
import { getRequestAdditionalInformationButtonLabel } from 'utils/additionalInfoFormCopy'

import { PreviousAppealRejectedRequestDetails } from './PreviousAppealApprovalDetails'
import { PreviousApprovalDetails } from './PreviousApprovalDetails'
import { PreviousAttestationRejectedRequestDetails } from './PreviousAttestationRejectedRequestDetails'

const { Header, Content } = Layout

interface GoUpProps {
  onClick(): Promise<void>
  currentDir: string
}

const GoUp: React.FC<GoUpProps> = ({ onClick, currentDir }) => {
  return (
    <GoUpContainer onClick={onClick}>
      <GoUpIconContainer>
        <icons.ArrowGoUp />
      </GoUpIconContainer>
      {currentDir}
    </GoUpContainer>
  )
}

const Results: React.FC = () => {
  const {
    resultId,
    result,
    isLoading,
    breadcrumbs,
    contents,
    openDir,
    goUpOneLevel,
    reports,
    bucket,
    prefix,
    provider,
    isFile,
    isAnalysis,
    isDataAccessRequestResult,
    isReadyForDownload,
    isApprovalRestricted,
    isFileViewDisabled,
    isFileActionsLoading,
    isParticipantFileDownloadDisabled,
    isParticipantFileDownloadLoading,
    isApprovalActionsVisible,
    showRejectButton,
    showAttestationButton,
    activeTab,
    setActiveTab,
    showMeta,
    showPreviousApprovalDetails,
    showPreviousAppealRejectedRequestDetails,
    showPreviousAttestationRejectedRequestDetails,
    isApprovalModalOpen,
    isRejectModalOpen,
    isAttestationModalOpen,
    selectedDownloadExpiryOptionKey,
    setSelectedDownloadExpiryOptionKey,
    onClickReject,
    onClickApprove,
    onClickRequestAttestation,
    onClickBack,
    getTitle,
    approvalModalTitle,
    rejectionModalTitle,
    rejectionModalDescription,
    rejectionModalOkTitle,
    handleApproveResult,
    handleRequestAttestation,
    closeApprovalModal,
    closeRejectModal,
    closeAttestationModal,
    approveResultV2Mutation,
    rejectResultMutation,
    requestAttestationMutation,
    handleOnClickRetryApproveResult,
    workspaceConfigurationQuery,
    labelsQuery,
    validateResultLabel,
    handleOnClickLabelMenuItem,
    handleAddResultLabel,
    handleDeleteResultLabel
  } = useResults()

  if (!resultId) return null

  return (
    <Layout layoutWidth={LayoutWidth.XLarge}>
      <Header
        hasSeparator={false}
        subtitle={
          <HeaderSubtitle>
            {isLoading ? (
              <SubtitleSkeletonWrapper>
                <Skeleton.Input active size="small" />
                <Skeleton.Input active size="small" />
                <Skeleton.Input active size="small" />
              </SubtitleSkeletonWrapper>
            ) : (
              result && <Tags result={result} />
            )}
          </HeaderSubtitle>
        }
        title={
          isLoading ? (
            <TitleSkeletonWrapper>
              <Skeleton.Input active size="small" block />
            </TitleSkeletonWrapper>
          ) : (
            getTitle()
          )
        }
        onClickGoBack={onClickBack}
        actions={
          isApprovalActionsVisible ? (
            <ButtonsWrapper>
              {showRejectButton && (
                <Button
                  data-testid={REJECT_BUTTON_TEST_ID}
                  type="secondary"
                  onClick={onClickReject}
                  loading={rejectResultMutation.isPending}
                  danger
                >
                  Reject
                </Button>
              )}
              {showAttestationButton && (
                <Button
                  data-testid={REQUEST_ATTESTATION_BUTTON_TEST_ID}
                  type="secondary"
                  onClick={onClickRequestAttestation}
                >
                  {getRequestAdditionalInformationButtonLabel()}
                </Button>
              )}
              <Button
                data-testid={APPROVE_BUTTON_TEST_ID}
                type="primary"
                onClick={onClickApprove}
                loading={isDataAccessRequestResult && approveResultV2Mutation.isPending}
              >
                Approve
              </Button>
            </ButtonsWrapper>
          ) : undefined
        }
      />

      <Layout.Separator />

      {isLoading ? (
        <Content>
          <LoadingWrapper data-testid="results-loader">
            <Loader />
          </LoadingWrapper>
        </Content>
      ) : (
        <StyledContent>
          <RequestDetailLayout
            aside={
              <>
                <RequestLabelsCard
                  addedLabels={result?.labels}
                  allLabels={labelsQuery.data?.data}
                  onAddResultLabel={handleAddResultLabel}
                  onValidateResultLabel={validateResultLabel}
                  onClickLabelMenuItem={handleOnClickLabelMenuItem}
                  onDeleteResultLabel={handleDeleteResultLabel}
                />
                {result && <RequestActivityCard source={toExportRequestActivitySource(result)} />}
              </>
            }
          >
            <ResultAlerts
              isApprovalRestricted={isApprovalRestricted}
              result={result}
              onClickRetry={result ? handleOnClickRetryApproveResult(result) : () => {}}
              isRetrying={approveResultV2Mutation.isPending}
              isRetryingDisabled={approveResultV2Mutation.isPending || workspaceConfigurationQuery.isLoading}
            />
            {isAnalysis && (
              <Tabs activeKey={activeTab} onChange={id => setActiveTab(id)}>
                <TabPane tabKey="tab1" tabName="Results">
                  <ResultContainer>
                    <CommonContainer>
                      {!!breadcrumbs.length && (
                        <ContentsTitle>
                          <GoUp onClick={goUpOneLevel} currentDir={breadcrumbs[breadcrumbs.length - 1]} />
                        </ContentsTitle>
                      )}
                      {contents && contents.length > 0 && (
                        <ResultsList
                          bucket={bucket || ''}
                          contents={contents}
                          isAnalysis={isAnalysis}
                          isApprovalRestricted={isApprovalRestricted}
                          isFileViewDisabled={isFileViewDisabled}
                          isFileActionsLoading={isFileActionsLoading}
                          isFile={isFile}
                          isLoading={isLoading}
                          isReadyForDownload={isReadyForDownload}
                          openDir={openDir}
                          owner={result?.requestingUser}
                          path={prefix || ''}
                          provider={provider || ''}
                          resultId={resultId}
                          storageLocation={result?.beforeCheckLocation ?? { provider, bucket }}
                        />
                      )}
                      {contents && contents.length === 0 && (
                        <StyledReportMessage>
                          <ReportMessage>The results of your analysis will appear here.</ReportMessage>
                        </StyledReportMessage>
                      )}
                    </CommonContainer>
                  </ResultContainer>
                </TabPane>
                <TabPane tabKey="tab2" tabName="Reports">
                  <Reports reports={reports} />
                </TabPane>
              </Tabs>
            )}

            {showMeta && result && (
              <>
                {(result?.contentDescription || result?.exportReason) && (
                  <ResultMetaV1
                    isFile={isFile}
                    contentDescription={result?.contentDescription ?? ''}
                    exportReason={result?.exportReason ?? ''}
                  />
                )}
                <ResultMeta
                  result={result}
                  isFile={isFile}
                  resultId={resultId}
                  bucket={bucket || ''}
                  path={prefix || ''}
                  isReadyForDownload={isReadyForDownload}
                  isApprovalRestricted={isApprovalRestricted}
                  isFileViewDisabled={isFileViewDisabled}
                  isFileActionsLoading={isFileActionsLoading}
                  isParticipantFileDownloadDisabled={isParticipantFileDownloadDisabled}
                  isParticipantFileDownloadLoading={isParticipantFileDownloadLoading}
                  contents={contents ?? []}
                />
              </>
            )}
            {showPreviousApprovalDetails && result && <PreviousApprovalDetails result={result} />}
            {showPreviousAppealRejectedRequestDetails && result && (
              <PreviousAppealRejectedRequestDetails result={result} />
            )}
            {showPreviousAttestationRejectedRequestDetails && result && (
              <PreviousAttestationRejectedRequestDetails result={result} />
            )}
          </RequestDetailLayout>
        </StyledContent>
      )}

      {isApprovalModalOpen && (
        <ApprovalModal
          action={handleApproveResult}
          description={<ApprovalModalDescription result={result} isAnalysis={isAnalysis} contents={contents} />}
          isError={approveResultV2Mutation.isError}
          isLoading={approveResultV2Mutation.isPending}
          isOpen={isApprovalModalOpen}
          okTitle="Yes, approve"
          onClose={closeApprovalModal}
          title={approvalModalTitle}
          content={
            <DownloadExpirySelector
              options={downloadExpiryOptions}
              value={selectedDownloadExpiryOptionKey.toString()}
              onChange={downloadExpiryOptionKey => setSelectedDownloadExpiryOptionKey(downloadExpiryOptionKey)}
            />
          }
        />
      )}
      {isRejectModalOpen && (
        <RejectionModal
          onClose={closeRejectModal}
          isOpen={isRejectModalOpen}
          isLoading={rejectResultMutation.isPending}
          action={(reason: string) => rejectResultMutation.mutate({ reason })}
          isError={rejectResultMutation.isError}
          title={rejectionModalTitle}
          description={rejectionModalDescription}
          okTitle={rejectionModalOkTitle}
        />
      )}
      <RequestAttestationModal
        isOpen={isAttestationModalOpen}
        isLoading={requestAttestationMutation.isPending}
        isError={requestAttestationMutation.isError}
        onClose={closeAttestationModal}
        onSubmit={handleRequestAttestation}
      />
    </Layout>
  )
}

export default Results
